import { ReactElement, ReactNode } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { InfoIcon } from '../../../../bombiix-icon';

export type CreatedModifiedPopoverPlacement = 'top' | 'bottom' | 'left' | 'right' | 'auto';

export interface CreatedModifiedPopoverProps {
  createdLabel: string;
  createdText: string;
  modifiedLabel: string;
  /** When falsy the modified line is omitted (mirrors showCreatedModifiedBy). */
  modifiedText?: string | undefined;
  placement?: CreatedModifiedPopoverPlacement;
  /** Aria label for the default icon trigger. Ignored when `children` is supplied. */
  ariaLabel?: string;
  /** Class for the default icon trigger so it matches its host (card action bar, drawer, etc.). */
  buttonClassName?: string;
  iconSize?: number;
  /** Custom trigger element. Overrides the default InfoIcon button (e.g. a menu row). */
  children?: ReactNode;
}

/**
 * PIM created/modified audit popover. Mirrors the Testing table's
 * showCreatedModifiedBy: a hover/focus OverlayTrigger revealing a Popover card
 * with Created / Modified stamps, reusing the shared `custom-popover
 * modified-popover` styling. Renders a default InfoIcon trigger unless a
 * custom `children` trigger is supplied.
 *
 * Superseded for new work by `Components/BombiixInfoPopover`, which carries the
 * master-design skin and takes an arbitrary `rows` array. This one is kept for
 * the three PIM cards already on the legacy `custom-popover` skin — migrate
 * them together rather than restyling them one at a time.
 */
export function CreatedModifiedPopover({
  createdLabel,
  createdText,
  modifiedLabel,
  modifiedText,
  placement = 'bottom',
  ariaLabel,
  buttonClassName = 'btn p-0',
  iconSize = 24,
  children,
}: CreatedModifiedPopoverProps): ReactElement {
  const trigger = children ?? (
    <button type="button" className={buttonClassName} aria-label={ariaLabel}>
      <InfoIcon size={iconSize} />
    </button>
  );
  return (
    <OverlayTrigger
      placement={placement}
      trigger={['hover', 'focus']}
      rootClose
      overlay={
        <Popover id="pim-created-modified-popover" className="custom-popover modified-popover">
          <Popover.Content>
            <p className="m-0 text-left">
              <strong className="pim-text-bold">{createdLabel}</strong> {createdText}
            </p>
            {modifiedText ? (
              <p className="text-left m-0">
                <strong className="pim-text-bold">{modifiedLabel}</strong> {modifiedText}
              </p>
            ) : null}
          </Popover.Content>
        </Popover>
      }
    >
      {trigger as ReactElement}
    </OverlayTrigger>
  );
}

export default CreatedModifiedPopover;
